// src/pages/MultiDocImport.jsx
// CAS 3 (« WhatsApp du client ») : l'Advisor/collectionneur dépose une PILE de
// fichiers (photos, factures, certificats). ARTKIV propose un regroupement —
// une œuvre par document OCR'd, photos en attente — que l'utilisateur AJUSTE
// avant de créer les fiches. « Moins de tri, moins de copier-coller. »
//
// Réutilise le moteur OCR (api.vision.artkiv.com), mapOcrToArtworkPatch,
// getOrCreateUnclassifiedCollection, et le pattern d'insert d'AddArtwork.
// Rien n'est créé sans validation (§12). i18n FR/EN/DE.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { mapOcrToArtworkPatch } from "../utils/mapOcrToArtworkPatch";
import { getOrCreateUnclassifiedCollection } from "../lib/unclassifiedCollection";
import AiDisclosure from "@/components/AiDisclosure";
import { FileText, Image as ImageIcon, Sparkles, Loader2, ArrowLeft, Check, Plus, X, Layers } from "lucide-react";

const OCR_API = "https://api.vision.artkiv.com/analyze-document";
const VISUAL_GROUP_API = "https://api.vision.artkiv.com/api/visual/group-similar-images";
const DOC_BUCKET = "artworks-documents";
const IMG_BUCKET = "artworks-images";
const MAX_FILES = 60;

const COPY = {
  fr: {
    back: "Retour", title: "Importer et regrouper",
    subtitle: "Déposez tout ce que vous avez reçu — photos, factures, certificats. ARTKIV propose un regroupement par œuvre ; vous ajustez avant de créer les fiches. Rien n'est créé sans votre validation.",
    drop: "Déposez vos fichiers", dropHint: "Photos, PDF, certificats — jusqu'à 60 fichiers",
    kindDoc: "Document", kindPhoto: "Photo", remove: "Retirer",
    analyze: "Analyser et regrouper", analyzing: "Analyse en cours…", noFiles: "Ajoutez d'abord des fichiers.",
    reviewTitle: "Vérifiez les regroupements", reviewSub: "Déplacez un fichier d'une œuvre à l'autre si nécessaire, ajoutez ou fusionnez des œuvres.",
    betaNotice: "Fonctionnalité en cours d'amélioration : le regroupement automatique peut manquer certains liens entre documents. Vérifiez et fusionnez les œuvres si besoin avant de créer les fiches.",
    newWork: "Nouvelle œuvre", unassigned: "Documents non attribués", assignTo: "Rattacher à…",
    mergeWith: "Fusionner avec…", mergeInto: (label) => `Fusionner dans « ${label} »`,
    detach: "→ Détacher en nouvelle œuvre",
    visualGrouped: (n) => `${n} photos semblent montrer la même œuvre — regroupées automatiquement, à vérifier.`,
    work: (n) => `Œuvre ${n}`, filesN: (n) => `${n} fichier${n > 1 ? "s" : ""}`,
    fields: { title: "Titre", artist: "Artiste", year: "Année", medium: "Technique", dimensions: "Dimensions", acquisition_price: "Prix" },
    collectionLabel: "Ajouter à la collection",
    create: (n) => `Créer ${n} œuvre${n > 1 ? "s" : ""}`, creating: "Création…",
    nothing: "Aucune œuvre à créer (attribuez au moins un fichier ou renseignez un titre).",
    createdTitle: "Créé", createdBody: (n) => `${n} fiche${n > 1 ? "s" : ""} créée${n > 1 ? "s" : ""}, documents et photos rattachés.`,
    seeCollection: "Voir ma collection", again: "Importer d'autres fichiers",
    ocrPartial: "Certains documents n'ont pas pu être lus ; vous pouvez tout de même regrouper et compléter.",
    empty: "(aucun fichier)",
  },
  en: {
    back: "Back", title: "Import and group",
    subtitle: "Drop everything you received — photos, invoices, certificates. ARTKIV proposes a grouping per artwork; you adjust before creating the records. Nothing is created without your validation.",
    drop: "Drop your files", dropHint: "Photos, PDF, certificates — up to 60 files",
    kindDoc: "Document", kindPhoto: "Photo", remove: "Remove",
    analyze: "Analyse and group", analyzing: "Analysing…", noFiles: "Add files first.",
    reviewTitle: "Check the groupings", reviewSub: "Move a file from one artwork to another if needed, add or merge artworks.",
    betaNotice: "This feature is still being improved: automatic grouping may miss links between documents. Please review and merge artworks if needed before creating the records.",
    newWork: "New artwork", unassigned: "Unassigned files", assignTo: "Assign to…",
    mergeWith: "Merge with…", mergeInto: (label) => `Merge into "${label}"`,
    detach: "→ Detach into new artwork",
    visualGrouped: (n) => `${n} photos seem to show the same artwork — grouped automatically, please check.`,
    work: (n) => `Artwork ${n}`, filesN: (n) => `${n} file${n > 1 ? "s" : ""}`,
    fields: { title: "Title", artist: "Artist", year: "Year", medium: "Medium", dimensions: "Dimensions", acquisition_price: "Price" },
    collectionLabel: "Add to collection",
    create: (n) => `Create ${n} artwork${n > 1 ? "s" : ""}`, creating: "Creating…",
    nothing: "No artwork to create (assign at least one file or enter a title).",
    createdTitle: "Created", createdBody: (n) => `${n} record${n > 1 ? "s" : ""} created, documents and photos attached.`,
    seeCollection: "View my collection", again: "Import more files",
    ocrPartial: "Some documents couldn't be read; you can still group and complete.",
    empty: "(no file)",
  },
  de: {
    back: "Zurück", title: "Importieren und gruppieren",
    subtitle: "Laden Sie alles hoch, was Sie erhalten haben — Fotos, Rechnungen, Zertifikate. ARTKIV schlägt eine Gruppierung pro Werk vor; Sie passen an, bevor die Akten erstellt werden. Nichts wird ohne Ihre Bestätigung erstellt.",
    drop: "Dateien ablegen", dropHint: "Fotos, PDF, Zertifikate — bis zu 60 Dateien",
    kindDoc: "Dokument", kindPhoto: "Foto", remove: "Entfernen",
    analyze: "Analysieren und gruppieren", analyzing: "Analyse läuft…", noFiles: "Zuerst Dateien hinzufügen.",
    reviewTitle: "Gruppierungen prüfen", reviewSub: "Datei bei Bedarf zwischen Werken verschieben, Werke hinzufügen oder zusammenführen.",
    betaNotice: "Diese Funktion wird noch weiterentwickelt: Die automatische Gruppierung kann Zusammenhänge zwischen Dokumenten übersehen. Bitte prüfen und Werke bei Bedarf zusammenführen, bevor Sie die Akten erstellen.",
    newWork: "Neues Werk", unassigned: "Nicht zugeordnete Dateien", assignTo: "Zuordnen zu…",
    mergeWith: "Zusammenführen mit…", mergeInto: (label) => `In „${label}" zusammenführen`,
    detach: "→ In neues Werk trennen",
    visualGrouped: (n) => `${n} Fotos scheinen dasselbe Werk zu zeigen — automatisch gruppiert, bitte prüfen.`,
    work: (n) => `Werk ${n}`, filesN: (n) => `${n} Datei${n > 1 ? "en" : ""}`,
    fields: { title: "Titel", artist: "Künstler", year: "Jahr", medium: "Technik", dimensions: "Maße", acquisition_price: "Preis" },
    collectionLabel: "Zur Sammlung hinzufügen",
    create: (n) => `${n} Werk${n > 1 ? "e" : ""} erstellen`, creating: "Erstellen…",
    nothing: "Kein Werk zu erstellen (mindestens eine Datei zuordnen oder Titel eingeben).",
    createdTitle: "Erstellt", createdBody: (n) => `${n} Akte${n > 1 ? "n" : ""} erstellt, Dokumente und Fotos angehängt.`,
    seeCollection: "Meine Sammlung ansehen", again: "Weitere Dateien importieren",
    ocrPartial: "Einige Dokumente konnten nicht gelesen werden; Sie können dennoch gruppieren und ergänzen.",
    empty: "(keine Datei)",
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

const uid = () => (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
const isPdf = (f) => /\.pdf$/i.test(f.name) || f.type === "application/pdf";

// Devine le type du document depuis son nom (même convention que BatchImport.jsx) —
// évite de tout classer "Facture" par défaut.
const DOC_TYPE_RULES = [
  { pattern: /certif|authenticit/i, type: "certificate" },
  { pattern: /constat|conservation|condition/i, type: "condition_report" },
  { pattern: /exposition|exhibit|catalog/i, type: "exhibition" },
  { pattern: /facture|invoice|achat|vente/i, type: "invoice" },
];
function guessDocType(filename) {
  for (const r of DOC_TYPE_RULES) if (r.pattern.test(filename)) return r.type;
  return "other";
}
const inS = { width: "100%", padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 13.5, fontFamily: "system-ui, sans-serif", outline: "none" };
const lbl = { display: "block", fontSize: 10.5, color: "#6b6b6b", marginBottom: 4, fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 };

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
function toEditable(m) {
  return {
    title: m.title || "", artist: m.artist || "", year: m.year || "",
    medium: m.medium || m.medium_clean || m.technique || "", dimensions: m.dimensions || "",
    acquisition_type: m.acquisition_type || m.sale_type || "",
    acquisition_date: m.acquisition_date || m.date || m.invoice_date || m.auction_sale_date || "",
    acquisition_price: m.acquisition_price || m.price || m.total_amount || m.auction_hammer_price || "",
    currency: m.currency || "", auction_house: m.auction_house || "",
    auction_lot_number: m.auction_lot_number || m.lot_number || "",
  };
}
const EDIT_KEYS = ["title", "artist", "year", "medium", "dimensions", "acquisition_price"];
// Champs complets produits par toEditable() — utilisé pour la fusion de groupes,
// qui doit préserver TOUS les champs OCR (acquisition_date, acquisition_type,
// currency...), pas seulement ceux affichés dans le formulaire de révision.
const ALL_FIELD_KEYS = [
  "title", "artist", "year", "medium", "dimensions",
  "acquisition_type", "acquisition_date", "acquisition_price",
  "currency", "auction_house", "auction_lot_number",
];

const normTitle = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

// Fusionne automatiquement les groupes détectés dont le titre extrait se recoupe
// clairement (l'un contient l'autre, ou identique) — cas fréquent quand deux documents
// du même dossier (ex: constat + facture) désignent la même œuvre sous des noms
// légèrement différents ("Greffe 2" / "FH31 « Greffe 2 »"). Ne touche pas aux titres
// vides ou trop courts (< 3 caractères) pour éviter les faux positifs.
function autoMergeSimilarGroups(groupsList, assignmentsMap) {
  const kept = [];
  const mergedInto = {};
  groupsList.forEach((g) => {
    const gTitle = normTitle(g.fields.title);
    const match = gTitle.length >= 3 && kept.find((k) => {
      const kTitle = normTitle(k.fields.title);
      return kTitle.length >= 3 && (kTitle === gTitle || kTitle.includes(gTitle) || gTitle.includes(kTitle));
    });
    if (match) {
      ALL_FIELD_KEYS.forEach((k) => { if (!String(match.fields[k] || "").trim() && g.fields[k]) match.fields[k] = g.fields[k]; });
      mergedInto[g.id] = match.id;
    } else {
      kept.push({ ...g, fields: { ...g.fields } });
    }
  });
  Object.keys(assignmentsMap).forEach((fid) => {
    const gid = assignmentsMap[fid];
    if (mergedInto[gid]) assignmentsMap[fid] = mergedInto[gid];
  });
  return kept;
}

export default function MultiDocImport() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);

  const [stage, setStage] = useState("upload"); // upload | review | done
  const [files, setFiles] = useState([]);       // { id, file, kind, groupId }
  const [groups, setGroups] = useState([]);      // { id, fields }
  const [busy, setBusy] = useState(false);
  const [partial, setPartial] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [doneN, setDoneN] = useState(0);
  const [doneCol, setDoneCol] = useState(null);
  const [visualGroupedCount, setVisualGroupedCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("collections").select("id, title").eq("client_id", user.id).order("created_at", { ascending: false })
      .then(({ data }) => { setCollections(data || []); if (data?.[0]) setCollectionId(data[0].id); });
  }, [user?.id]);

  const addFiles = (list) => {
    const arr = Array.from(list || []).slice(0, MAX_FILES - files.length);
    setFiles((prev) => [...prev, ...arr.map((f) => ({ id: uid(), file: f, kind: isPdf(f) ? "document" : "photo", groupId: null }))]);
  };
  const setKind = (id, kind) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, kind } : f)));
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));
  const assign = (fileId, groupId) => setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, groupId } : f)));

  // Détache un fichier d'un (mauvais) regroupement en le sortant dans sa propre
  // nouvelle œuvre vide, en un clic (sinon il faudrait créer "Nouvelle œuvre" puis
  // rouvrir le menu du fichier pour l'y réattribuer).
  const detachToNewGroup = (fileId) => {
    const gid = uid();
    setGroups((prev) => [...prev, { id: gid, fields: toEditable({}) }]);
    assign(fileId, gid);
  };

  // N'est appelé que quand c'est utile : plusieurs œuvres candidates ET des photos
  // encore sans attribution évidente — sinon inutile de dépenser un appel Vision.
  // SUGGESTION uniquement (regroupe les photos entre elles) : ne relie jamais une
  // photo à un groupe texte automatiquement, l'utilisateur valide toujours ensuite.
  const groupSimilarPhotos = useCallback(async (photoFiles, token) => {
    if (photoFiles.length < 2) return [];
    try {
      const images = await Promise.all(photoFiles.map((pf) => fileToBase64(pf.file)));
      const res = await fetch(VISUAL_GROUP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ images, client_id: user?.id || null }),
      });
      if (!res.ok) return [];
      const json = await res.json();
      return Array.isArray(json.groups) ? json.groups : [];
    } catch {
      return [];
    }
  }, [user?.id]);

  const analyze = useCallback(async () => {
    if (!files.length) { alert(tx.noFiles); return; }
    setBusy(true); setPartial(false); setVisualGroupedCount(0);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const docFiles = files.filter((f) => f.kind === "document");
      const newGroups = [];
      const assignments = {}; // fileId -> groupId
      let hadError = false;

      for (const df of docFiles) {
        let fields = toEditable({});
        try {
          const b64 = await fileToBase64(df.file);
          const res = await fetch(OCR_API, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ file: b64, document_type: ["certificate", "condition_report", "invoice"].includes(guessDocType(df.file.name)) ? guessDocType(df.file.name) : "auto", client_id: user?.id || null }),
          });
          if (!res.ok) throw new Error(String(res.status));
          const json = await res.json();
          const extracted = json.extracted_data || {};
          const first = Array.isArray(json.artworks) && json.artworks[0] ? json.artworks[0] : {};
          fields = toEditable({ ...extracted, ...first });
        } catch {
          hadError = true;
        }
        const gid = uid();
        newGroups.push({ id: gid, fields });
        assignments[df.id] = gid;
      }

      const dedupedGroups = autoMergeSimilarGroups(newGroups, assignments);

      const photoFiles = files.filter((f) => f.kind === "photo");
      // Une seule œuvre détectée → on y rattache toutes les photos. Sinon photos en attente.
      if (dedupedGroups.length === 1) {
        photoFiles.forEach((pf) => { assignments[pf.id] = dedupedGroups[0].id; });
      } else if (dedupedGroups.length === 0 && photoFiles.length) {
        const gid = uid();
        dedupedGroups.push({ id: gid, fields: toEditable({}) });
        photoFiles.forEach((pf) => { assignments[pf.id] = gid; });
      }

      // Cas ambigu : plusieurs œuvres candidates ET des photos encore sans
      // attribution évidente → on demande à la vision de les regrouper ENTRE ELLES
      // (jamais avec un groupe texte) ; l'utilisateur choisit ensuite où les rattacher.
      const stillUnassignedPhotos = photoFiles.filter((pf) => !assignments[pf.id]);
      if (dedupedGroups.length > 1 && stillUnassignedPhotos.length >= 2) {
        const visualGroups = await groupSimilarPhotos(stillUnassignedPhotos, token);
        let groupedN = 0;
        visualGroups.forEach((idxGroup) => {
          if (idxGroup.length < 2) return;
          const gid = uid();
          dedupedGroups.push({ id: gid, fields: toEditable({}) });
          idxGroup.forEach((idx) => {
            const pf = stillUnassignedPhotos[idx];
            if (pf) { assignments[pf.id] = gid; groupedN += 1; }
          });
        });
        if (groupedN) setVisualGroupedCount(groupedN);
      }

      setGroups(dedupedGroups);
      setFiles((prev) => prev.map((f) => ({ ...f, groupId: assignments[f.id] ?? f.groupId ?? null })));
      setPartial(hadError);
      setStage("review");
    } catch (e) {
      console.warn("[MultiDocImport] analyze", e);
      alert(tx.noFiles);
    } finally {
      setBusy(false);
    }
  }, [files, tx, user?.id, groupSimilarPhotos]);

  const addGroup = () => setGroups((prev) => [...prev, { id: uid(), fields: toEditable({}) }]);
  const setGroupField = (gid, key, val) => setGroups((prev) => prev.map((g) => (g.id === gid ? { ...g, fields: { ...g.fields, [key]: val } } : g)));
  const removeGroup = (gid) => {
    setFiles((prev) => prev.map((f) => (f.groupId === gid ? { ...f, groupId: null } : f)));
    setGroups((prev) => prev.filter((g) => g.id !== gid));
  };

  // Fusionne deux groupes reconnus comme la même œuvre (l'auto-détection ne les avait
  // pas rapprochés) : déplace les fichiers de sourceGid vers targetGid, complète les
  // champs vides de la cible avec ceux de la source, puis retire le groupe source.
  const mergeGroup = (sourceGid, targetGid) => {
    if (!targetGid || sourceGid === targetGid) return;
    setFiles((prev) => prev.map((f) => (f.groupId === sourceGid ? { ...f, groupId: targetGid } : f)));
    setGroups((prev) => {
      const source = prev.find((g) => g.id === sourceGid);
      return prev
        .filter((g) => g.id !== sourceGid)
        .map((g) => {
          if (g.id !== targetGid || !source) return g;
          const fields = { ...g.fields };
          ALL_FIELD_KEYS.forEach((k) => { if (!String(fields[k] || "").trim() && source.fields[k]) fields[k] = source.fields[k]; });
          return { ...g, fields };
        });
    });
  };

  const filesByGroup = useMemo(() => {
    const m = {};
    files.forEach((f) => { const k = f.groupId || "__none"; (m[k] = m[k] || []).push(f); });
    return m;
  }, [files]);

  const creatableGroups = useMemo(
    () => groups.filter((g) => (filesByGroup[g.id]?.length || 0) > 0 || String(g.fields.title || "").trim()),
    [groups, filesByGroup]
  );

  const groupLabel = (g, idx) => g.fields.title?.trim() || g.fields.artist?.trim() || tx.work(idx + 1);

  const create = useCallback(async () => {
    if (!user?.id) return;
    if (!creatableGroups.length) { alert(tx.nothing); return; }
    setBusy(true);
    try {
      const colId = collectionId || (await getOrCreateUnclassifiedCollection(user.id));
      for (const g of creatableGroups) {
        const gFiles = filesByGroup[g.id] || [];
        // Upload documents + photos du groupe
        const docRefs = []; const images = [];
        for (const gf of gFiles) {
          const safe = gf.file.name.replace(/[^\w.-]+/g, "_");
          if (gf.kind === "document") {
            const path = `${user.id}/${uid()}-${safe}`;
            const { error } = await supabase.storage.from(DOC_BUCKET).upload(path, gf.file, { upsert: false });
            if (!error) docRefs.push({ name: gf.file.name, type: guessDocType(gf.file.name), storagePath: path, bucket: DOC_BUCKET });
          } else {
            const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}-${safe}`;
            const { error } = await supabase.storage.from(IMG_BUCKET).upload(path, gf.file);
            if (!error) {
              const { data: pub } = supabase.storage.from(IMG_BUCKET).getPublicUrl(path);
              images.push({ id: Date.now() + Math.random(), path: pub.publicUrl, url: pub.publicUrl, isPrimary: images.length === 0 });
            }
          }
        }
        const patch = mapOcrToArtworkPatch({ extracted: g.fields, artwork: {} });
        const srcName = gFiles.find((f) => f.kind === "document")?.file?.name || null;
        const provSource = srcName ? { source: srcName, source_type: "document", status: "extracted", at: new Date().toISOString() } : null;
        const provenance = {};
        if (provSource) Object.entries(g.fields).forEach(([k, v]) => { if (String(v || "").trim()) provenance[k] = provSource; });
        const payload = {
          ...patch,
          title: patch.title || g.fields.title || tx.work(creatableGroups.indexOf(g) + 1),
          client_id: user.id, collection_id: colId,
          documents: docRefs.length ? JSON.stringify(docRefs) : null,
          images,
          field_provenance: Object.keys(provenance).length ? provenance : {},
        };
        const { error } = await supabase.from("artworks").insert([payload]);
        if (error) throw error;
      }
      setDoneN(creatableGroups.length);
      setDoneCol(colId);
      setStage("done");
    } catch (e) {
      console.warn("[MultiDocImport] create", e);
      alert(e?.message || "");
    } finally {
      setBusy(false);
    }
  }, [user?.id, creatableGroups, filesByGroup, collectionId, tx]);

  if (stage === "done") {
    return (
      <div style={{ background: "#faf8f4", padding: "48px 20px", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ maxWidth: 560, margin: "0 auto", background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 32, textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f1f6f1", color: "#2c5f2d", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}><Check size={26} /></div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 400, color: "#1a1a1a", margin: 0 }}>{tx.createdTitle}</h1>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "8px 0 20px" }}>{tx.createdBody(doneN)}</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button type="button" onClick={() => navigate(`/collections/${doneCol}`)} style={{ padding: "11px 18px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>{tx.seeCollection}</button>
            <button type="button" onClick={() => { setFiles([]); setGroups([]); setDoneN(0); setStage("upload"); }} style={{ padding: "11px 18px", background: "#fff", color: "#1a1a1a", border: "1px solid #e5e3df", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>{tx.again}</button>
          </div>
        </div>
      </div>
    );
  }

  const groupOptions = groups.map((g, i) => ({ id: g.id, label: groupLabel(g, i) }));

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f4", padding: "32px 20px", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#6b6b6b", fontSize: 13, cursor: "pointer", marginBottom: 16 }}><ArrowLeft size={16} /> {tx.back}</button>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>{tx.title}</h1>
        <p style={{ fontSize: 14.5, color: "#6b6b6b", lineHeight: 1.6, margin: "8px 0 12px", maxWidth: 660 }}>{tx.subtitle}</p>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", background: "#faf6ea", border: "1px solid #ecdcae", borderRadius: 8, fontSize: 12.5, color: "#8b7355", lineHeight: 1.5, maxWidth: 660, marginBottom: 24 }}>
          <Sparkles size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{tx.betaNotice}</span>
        </div>

        {stage === "upload" && (
          <div style={{ display: "grid", gap: 16 }}>
            <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "28px 16px", border: "1.5px dashed #d6d0c4", borderRadius: 10, cursor: "pointer", background: "#fff" }}>
              <Layers size={26} color="#8b7355" />
              <span style={{ fontSize: 15, color: "#1a1a1a", fontWeight: 600 }}>{tx.drop}</span>
              <span style={{ fontSize: 12.5, color: "#9ca3af" }}>{tx.dropHint}</span>
              <input type="file" multiple accept=".pdf,image/*" onChange={(e) => addFiles(e.target.files)} style={{ display: "none" }} />
            </label>

            {files.length > 0 && (
              <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 14, display: "grid", gap: 8 }}>
                {files.map((f) => (
                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", borderBottom: "1px solid #f5f5f4" }}>
                    {f.kind === "document" ? <FileText size={16} color="#8b7355" /> : <ImageIcon size={16} color="#8b7355" />}
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.file.name}</span>
                    <select value={f.kind} onChange={(e) => setKind(f.id, e.target.value)} style={{ ...inS, width: "auto", fontSize: 12.5 }}>
                      <option value="document">{tx.kindDoc}</option>
                      <option value="photo">{tx.kindPhoto}</option>
                    </select>
                    <button type="button" onClick={() => removeFile(f.id)} style={{ background: "transparent", border: "none", color: "#c0bcb2", cursor: "pointer" }}><X size={15} /></button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <button type="button" disabled={busy || !files.length} onClick={analyze}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 22px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (busy || !files.length) ? "not-allowed" : "pointer", opacity: (busy || !files.length) ? 0.55 : 1 }}>
                {busy ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />} {busy ? tx.analyzing : tx.analyze}
              </button>
            </div>
          </div>
        )}

        {stage === "review" && (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontFamily: "Georgia, serif", fontSize: 18, color: "#1a1a1a" }}>{tx.reviewTitle}</div>
              <div style={{ fontSize: 13, color: "#6b6b6b", marginTop: 3 }}>{tx.reviewSub}</div>
              {partial && <div style={{ fontSize: 12.5, color: "#8a6a2f", marginTop: 6 }}>{tx.ocrPartial}</div>}
              {visualGroupedCount > 0 && <div style={{ fontSize: 12.5, color: "#8a6a2f", marginTop: 6 }}>{tx.visualGrouped(visualGroupedCount)}</div>}
              <AiDisclosure type="extracted" compact style={{ marginTop: 10 }} />
            </div>

            {groups.map((g, idx) => {
              const gFiles = filesByGroup[g.id] || [];
              return (
                <div key={g.id} style={{ background: "#fff", border: "1px solid #c9a961", borderRadius: 10, padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                    <div style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1a1a1a" }}>{groupLabel(g, idx)} · <span style={{ fontFamily: "system-ui", fontSize: 12.5, color: "#9ca3af" }}>{tx.filesN(gFiles.length)}</span></div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {groups.length > 1 && (
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) mergeGroup(g.id, e.target.value); }}
                          title={tx.mergeWith}
                          style={{ border: "1px solid #ecdcae", background: "#faf6ea", color: "#8b7355", fontSize: 11.5, borderRadius: 6, padding: "3px 6px", cursor: "pointer" }}
                        >
                          <option value="">{tx.mergeWith}</option>
                          {groups.filter((o) => o.id !== g.id).map((o, i) => (
                            <option key={o.id} value={o.id}>{tx.mergeInto(groupLabel(o, groups.indexOf(o)))}</option>
                          ))}
                        </select>
                      )}
                      <button type="button" onClick={() => removeGroup(g.id)} style={{ background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 12 }}><X size={14} /></button>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))", gap: 10, marginBottom: 12 }}>
                    {EDIT_KEYS.map((k) => (
                      <div key={k}><label style={lbl}>{tx.fields[k]}</label><input value={g.fields[k] ?? ""} onChange={(e) => setGroupField(g.id, k, e.target.value)} style={inS} /></div>
                    ))}
                  </div>
                  {gFiles.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {gFiles.map((f) => (
                        <span key={f.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 8px", background: "#faf6ea", border: "1px solid #ecdcae", borderRadius: 999, fontSize: 11.5, color: "#8b7355" }}>
                          {f.kind === "document" ? <FileText size={11} /> : <ImageIcon size={11} />}
                          <span style={{ maxWidth: 140, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.file.name}</span>
                          <select
                            value={f.groupId || ""}
                            onChange={(e) => { if (e.target.value === "__new__") detachToNewGroup(f.id); else assign(f.id, e.target.value || null); }}
                            style={{ border: "none", background: "transparent", fontSize: 11, color: "#8b7355", cursor: "pointer" }}
                            title={tx.assignTo}
                          >
                            {groupOptions.map((o, i) => <option key={o.id} value={o.id}>{tx.work(i + 1)}: {o.label}</option>)}
                            {gFiles.length > 1 && <option value="__new__">{tx.detach}</option>}
                            <option value="">— {tx.unassigned}</option>
                          </select>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <button type="button" onClick={addGroup} style={{ display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "start", padding: "9px 14px", background: "#fff", color: "#8b7355", border: "1px dashed #c9a961", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Plus size={15} /> {tx.newWork}</button>

            {/* Non attribués */}
            {(filesByGroup.__none?.length || 0) > 0 && (
              <div style={{ background: "#fafaf8", border: "1px solid #e5e3df", borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontWeight: 700, marginBottom: 10 }}>{tx.unassigned} · {filesByGroup.__none.length}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {filesByGroup.__none.map((f) => (
                    <span key={f.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 9px", background: "#fff", border: "1px solid #e5e3df", borderRadius: 999, fontSize: 11.5, color: "#6b6b6b" }}>
                      {f.kind === "document" ? <FileText size={11} /> : <ImageIcon size={11} />}
                      <span style={{ maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.file.name}</span>
                      <select value="" onChange={(e) => e.target.value && assign(f.id, e.target.value)} style={{ border: "none", background: "transparent", fontSize: 11, color: "#3a4a6b", cursor: "pointer" }}>
                        <option value="">{tx.assignTo}</option>
                        {groupOptions.map((o, i) => <option key={o.id} value={o.id}>{tx.work(i + 1)}: {o.label}</option>)}
                      </select>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 16 }}>
              <label style={lbl}>{tx.collectionLabel}</label>
              <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)} style={inS}>
                {collections.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>

            <div>
              <button type="button" disabled={busy || !creatableGroups.length} onClick={create}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (busy || !creatableGroups.length) ? "not-allowed" : "pointer", opacity: (busy || !creatableGroups.length) ? 0.55 : 1 }}>
                {busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />} {busy ? tx.creating : tx.create(creatableGroups.length)}
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
